import { useState } from "react";
import { Link } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import {
  useListFavorites,
  useListFavoriteCollections,
  useCreateFavoriteCollection,
  getListFavoriteCollectionsQueryKey,
} from "@workspace/api-client-react";
import type { RecipeRecord } from "@workspace/api-client-react";
import { Layout } from "@/components/Layout";
import { RecipeCard } from "@/components/RecipeCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { Heart, Folder, FolderPlus, ChevronRight } from "lucide-react";

function CollectionsSection() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");

  const { data, isLoading } = useListFavoriteCollections();
  const collections = data?.collections ?? [];

  const createMut = useCreateFavoriteCollection({
    mutation: {
      onSuccess: () => {
        toast({ title: "Collection created" });
        void qc.invalidateQueries({
          queryKey: getListFavoriteCollectionsQueryKey(),
        });
        setName("");
        setOpen(false);
      },
      onError: () =>
        toast({ title: "Could not create collection", variant: "destructive" }),
    },
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    createMut.mutate({ data: { name: trimmed } });
  };

  return (
    <section>
      <div className="flex items-center justify-between mb-3">
        <h2
          className="text-lg font-semibold text-foreground flex items-center gap-2"
          style={{ fontFamily: "var(--font-display)" }}
        >
          <Folder className="w-4 h-4 text-primary" />
          Collections
        </h2>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" variant="outline" className="gap-1.5 h-8">
              <FolderPlus className="w-3.5 h-3.5" />
              New collection
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create a new collection</DialogTitle>
              <DialogDescription>
                Group favorited recipes by occasion or theme — e.g.
                “Weeknight dinners” or “Christmas cookies”.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <Input
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Collection name"
                maxLength={100}
              />
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createMut.isPending || name.trim().length === 0}
                >
                  Create
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="h-20 rounded-2xl bg-muted animate-pulse"
            />
          ))}
        </div>
      ) : collections.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-muted/40 px-4 py-6 text-center text-sm text-muted-foreground">
          No collections yet — create one to group recipes by occasion or
          theme.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {collections.map((c) => (
            <Link
              key={c.id}
              href={`/favorites/collections/${c.id}`}
              className="group flex items-center justify-between gap-3 rounded-2xl border border-border bg-card hover:border-primary/40 hover:bg-primary/5 transition-colors px-4 py-3 min-w-0"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="bg-primary/10 p-2 rounded-xl shrink-0">
                  <Folder className="w-4 h-4 text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-sm text-foreground truncate">
                    {c.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {c.recipeCount} recipe{c.recipeCount === 1 ? "" : "s"}
                  </p>
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

export default function FavoritesPage() {
  const { data, isLoading, error } = useListFavorites();
  const recipes: RecipeRecord[] = data?.recipes ?? [];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        <header className="flex items-center gap-3 pt-2">
          <div className="bg-rose-50 p-3 rounded-2xl border border-rose-100">
            <Heart className="w-6 h-6 text-rose-500 fill-rose-500" />
          </div>
          <div>
            <h1
              className="text-3xl font-bold text-foreground"
              style={{ fontFamily: "var(--font-display)" }}
            >
              Your Favorites
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {isLoading
                ? "Loading…"
                : recipes.length === 0
                  ? "No favorites yet — tap the heart on any recipe to save it here."
                  : `${recipes.length} recipe${recipes.length === 1 ? "" : "s"} you reach for again and again.`}
            </p>
          </div>
        </header>

        <CollectionsSection />

        <section>
          <h2
            className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2"
            style={{ fontFamily: "var(--font-display)" }}
          >
            <Heart className="w-4 h-4 text-rose-500 fill-rose-500" />
            All favorites
          </h2>

          {isLoading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="aspect-[4/5] rounded-2xl bg-muted animate-pulse"
                />
              ))}
            </div>
          ) : error ? (
            <div className="py-16 text-center text-muted-foreground">
              Could not load favorites. Please refresh the page.
            </div>
          ) : recipes.length === 0 ? (
            <div className="py-16 text-center">
              <Link
                href="/"
                className="inline-block px-5 py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold text-sm hover:opacity-90 transition-opacity"
              >
                Browse the cookbook
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {recipes.map((r) => (
                <RecipeCard key={r.id} recipe={r} />
              ))}
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
}
